// Chat composer — thin wrapper around the shared ComposerCore (MR B).
// All composer primitives (textarea/keyboard/paste/upload/chips/send/stop)
// live in ComposerCore; SearchBar adds ONLY the chat-specific model
// selector. Behavior is the canonical chat composer, unchanged:
// screenshot paste, attachment-only send, Enter/Shift+Enter, stop.
import { useRef, useState, useEffect } from "react";
import { Cpu } from "lucide-react";
import { cn } from "@/lib/utils";
import { ComposerCore } from "@/components/composer/ComposerCore";
import { SmartSearchChip } from "@/components/SmartSearchChip";
import type { AttachmentPayload, SelectorModel, WebSearchMode, WebStatusPayload } from "@/lib/api";

interface SearchBarProps {
  onSearch: (query: string, attachments?: AttachmentPayload[]) => void;
  isLoading?: boolean;
  selectedModel: string;
  onModelChange: (model: string) => void;
  // F1 (decision #2): entitlement-filtered models + display names (B1).
  models: SelectorModel[];
  // F3 (decision #13): UI trigger for the existing abort (handleStop).
  onStop?: () => void;
  // Library attachment: files uploaded via "+" go to the user library
  // (50MB quota) and are sent with the message.
  onLibraryChanged?: () => void;
  // WG-3.1 Smart Search: composer web-mode chip (auto | on | off).
  webMode: WebSearchMode;
  onWebModeChange: (mode: WebSearchMode) => void;
  // Live search status for the current streaming turn (§29).
  webStatus?: WebStatusPayload | null;
}

export function SearchBar({
  onSearch,
  isLoading,
  selectedModel,
  onModelChange,
  models,
  onStop,
  onLibraryChanged,
  webMode,
  onWebModeChange,
  webStatus = null,
}: SearchBarProps) {
  const [showModelMenu, setShowModelMenu] = useState(false);
  const modelMenuRef = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  // Close the model menu on outside click.
  useEffect(() => {
    if (!showModelMenu) return;
    const handler = (e: MouseEvent) => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(e.target as Node)) {
        setShowModelMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showModelMenu]);

  // Auto-flip: open the model menu upward when there is not enough room
  // below (the composer sits at the bottom of the screen).
  const toggleModelMenu = () => {
    const willOpen = !showModelMenu;
    if (willOpen && modelMenuRef.current) {
      const rect = modelMenuRef.current.getBoundingClientRect();
      const estHeight = Math.min(models.length * 36 + 40, 320) + 16;
      const roomBelow = window.innerHeight - rect.bottom;
      setFlipUp(roomBelow < estHeight && rect.top > estHeight + 16);
    }
    setShowModelMenu(willOpen);
  };

  // F1: show the human-readable display name (not the raw gguf path).
  const selectedLabel =
    models.find((m) => m.name === selectedModel)?.display ||
    selectedModel ||
    "Model";

  return (
    <ComposerCore
      onSend={onSearch}
      placeholder="Введите сообщение..."
      isLoading={isLoading}
      onStop={onStop}
      onLibraryChanged={onLibraryChanged}
      leftSlot={
        <div className="flex items-center gap-1 min-w-0">
        <div className="relative" ref={modelMenuRef}>
          <button
            data-testid="composer-model-selector"
            onClick={toggleModelMenu}
            className="flex items-center gap-1.5 pl-2 pr-1 h-8 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg transition-colors max-w-[220px]"
          >
            <Cpu size={16} className="shrink-0" />
            <span className="truncate text-sm">{selectedLabel}</span>
          </button>
          {showModelMenu && (
            <div
              className={cn(
                "absolute left-0 w-64 max-w-[calc(100vw-64px)] bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30",
                flipUp ? "bottom-full mb-2" : "top-full mt-2"
              )}
            >
              <div className="max-h-[320px] overflow-y-auto">
                <div className="px-4 py-2 text-xs text-tertiary uppercase tracking-wider border-b border-hairline sticky top-0 bg-elevated">
                  Выберите модель
                </div>
                {models.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-tertiary">
                    Нет доступных моделей
                  </div>
                ) : (
                  models.map((model) => (
                    <ModelOption
                      key={model.name}
                      label={model.display}
                      isFree={model.isFree}
                      active={model.name === selectedModel}
                      onClick={() => {
                        onModelChange(model.name);
                        setShowModelMenu(false);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        {/* WG-3.1 Smart Search chip — next to the model selector. */}
        <SmartSearchChip
          mode={webMode}
          onModeChange={onWebModeChange}
          searchStatus={
            webStatus === null
              ? null
              : webStatus.status === "enabled"
                ? webStatus.count
                  ? "found"
                  : webStatus.operation === "reader"
                    ? "reading"
                    : "searching"
                : webStatus.status === "degraded" || webStatus.status === "unavailable"
                  ? "degraded"
                  : null
          }
          searchCount={webStatus?.count}
        />
        </div>
      }
    />
  );
}

function ModelOption({
  label,
  isFree,
  active,
  onClick,
}: {
  label: string;
  isFree: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-2 text-sm hover:bg-hoverbg text-primary flex items-center justify-between gap-2"
    >
      <span className="truncate">{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        <span
          className={cn(
            "text-[10px] font-semibold px-1.5 py-0.5 rounded",
            isFree
              ? "bg-emerald-500/15 text-emerald-400"
              : "bg-accent/15 text-accent"
          )}
        >
          {isFree ? "FREE" : "PRO"}
        </span>
        {active && <div className="w-2 h-2 rounded-full bg-accent" />}
      </span>
    </button>
  );
}
